import { Chips, FormField, InputAdornment, NumberInput } from '@cian/ui-kit';
import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { type IField, type IFormState } from '../../types/calculator';
import { getFieldPlaceholder } from '../../utils/getFieldPlaceholder';
import { useBaseField } from '../BaseField';
import styles from './styles.css';

export const InputWithOptions: React.FC<IField> = props => {
  const { calculateForField, calculateForChips, source } = props;
  const {
    field: { onChange, name, value: fieldValue, ref },
    fieldState,
  } = useBaseField(props);
  const { getValues, control } = useFormContext<IFormState>();
  const objectCostWatched = useWatch({ name: 'objectCost', control });
  const currentValue = fieldValue == null ? undefined : Number(fieldValue);

  const handleChangeOption = useCallback(
    (v: string) => {
      const formState = getValues() as IFormState;
      const resultValueForInput = calculateForField ? calculateForField(Number(v), formState) : Number(v);

      onChange(resultValueForInput);
    },
    [calculateForField, getValues, onChange],
  );

  const chipValue = useMemo(() => {
    if (currentValue == null || Number.isNaN(currentValue)) {
      return null;
    }
    if (calculateForChips) {
      const state = getValues() as IFormState;

      return calculateForChips(currentValue, state).toString();
    }

    return currentValue.toString();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateForChips, currentValue, getValues, objectCostWatched]);

  const handleChangeInput = useCallback(
    async (inputValue: number | undefined) => {
      const v =
        inputValue == null || (typeof inputValue === 'number' && Number.isNaN(inputValue)) ? undefined : inputValue;
      await onChange(v);
    },
    [onChange],
  );

  if (!source) {
    return null;
  }

  const isError = fieldState.isDirty || fieldState.invalid;
  const isAllChipsHidden = source.filter(({ hidden }) => hidden).length === source.length;

  const options = source.map(option => {
    return {
      value: option.value,
      label: option.label,
      disabled: option.disabled,
      dataTestId: option.dataTestId,
    };
  });

  // Chips определяет выбранный чип по value.includes(optionValue). Опции имеют значения "20", "30" и т.д.
  // Мы уже считаем checked в схеме, поэтому используем value отмеченной опции,
  // чтобы чип оставался выбранным (например, после клика 20% → 20.1%).
  const checkedIndex = source.findIndex(option => option.checked);
  const chipsValue = checkedIndex >= 0 ? source[checkedIndex].value : chipValue;

  return (
    <>
      <FormField
        label={props.label}
        errorMessage={isError ? fieldState.error : undefined}
        required={!!props.validation?.required}
        withoutMargin
      >
        <NumberInput
          data-testid={name}
          value={currentValue}
          onChange={handleChangeInput}
          placeholder={getFieldPlaceholder(props.placeholder)}
          invalid={Boolean(fieldState.isDirty && fieldState.error)}
          disabled={props.disabled}
          name={name}
          ref={ref}
          rightAdornment={<InputAdornment>{props.rightAdornment}</InputAdornment>}
          decimalDigits={props.decimalDigits}
          size="M"
          min={0}
        />
      </FormField>
      {!isAllChipsHidden && (
        <div className={styles.chips}>
          <Chips options={options} value={[chipsValue]} onChipClick={handleChangeOption} />
        </div>
      )}
    </>
  );
};

InputWithOptions.displayName = 'InputWithOptions';
